import { ChartType, ColumnTypeEnum } from '@y42/json-schema';
import { ChartControlTypeEnum } from './chart-control-type.enum';
import {
  CohortSettingsModel,
  FunnelSettingsModel,
  GeoSettingsModel,
  NumericWidgetSettingsModel,
  PieSettingsModel,
  ScatterSettingsModel,
  SimpleEchartsSettingsModel,
  TableSettingsModel,
  TreemapSettingsModel,
} from './chart-models';
import {
  AvailableAllChartType,
  ChartTypeConstantAvailableAreas,
  ChartValidationResult,
  IChartTypeConstant,
  IChartTypeConstantModelBase,
} from './chart-type-constant.model';
import { ColumnDropButtonTypeEnum } from './column-drop-button-type.enum';
import type { DropAreaConfigModel } from './drop-area-config.model';
import { fieldId } from './field-id.model';
import type { WidgetBuilderModel } from './widget-builder.model';

export const validate: <T extends AvailableAllChartType>(
  state: WidgetBuilderModel<T>,
  availableAreasOverride?: Partial<Record<ChartTypeConstantAvailableAreas, DropAreaConfigModel>>,
) => ChartValidationResult = (state, availableAreasOverride) => {
  const { chart_type, settings, fields } = state;
  const { availableAreas } = AllChartTypeConstant[chart_type]!;
  const flexibleSettings = settings as unknown as Record<
    ChartTypeConstantAvailableAreas,
    Array<Record<string, unknown>> | Record<string, unknown>
  >;

  function hasFieldId(input: Record<string, unknown>): input is { field_id: fieldId } {
    return input?.hasOwnProperty('field_id');
  }
  function notLinkedToUnsetCalculatedFields(input: Record<string, unknown>): boolean {
    if (hasFieldId(input)) {
      const matchedField = fields?.find((f) => f.id === input.field_id);
      if (matchedField) {
        return matchedField.lowCodeString !== '';
      }
    }
    return true;
  }

  const validateArray = (area: ChartTypeConstantAvailableAreas, min?: number, max?: number) => {
    const arr = (flexibleSettings[area] as Array<Record<string, unknown>>).filter(notLinkedToUnsetCalculatedFields);
    if (!arr) {
      return null;
    }

    if (min && max) {
      return arr.length >= min && arr.length <= max ? null : arr.length < min ? { missing: area } : { tooMany: area };
    }

    if (min) {
      return arr.length >= min ? null : { missing: area };
    }

    if (max) {
      return arr.length <= max ? null : { tooMany: area };
    }

    return null;
  };

  // TODO: can be remove when we get rid of the regresstion charts
  const validateObject = (area: ChartTypeConstantAvailableAreas, min?: number, max?: number) => {
    const object = flexibleSettings[area] as Record<string, unknown>;
    if ((min && min > 1) || (max && max > 1)) {
      throw new Error('Expecting more than 1 value while an object was given');
    }
    if (min === 1) {
      return notLinkedToUnsetCalculatedFields(object) ? null : { missing: area };
    }
    return null;
  };

  return Object.entries(availableAreasOverride || availableAreas).reduce((result, [area, cfg]) => {
    const { min, max } = cfg;
    const areaAsAvailableArea = area as ChartTypeConstantAvailableAreas;
    let error: ChartValidationResult;
    if (Array.isArray(flexibleSettings[areaAsAvailableArea])) {
      error = validateArray(areaAsAvailableArea, min, max);
    } else {
      error = validateObject(areaAsAvailableArea, min, max);
    }
    return result || error;
  }, null as ChartValidationResult);
};

export const EchartTypeConstant: Partial<IChartTypeConstant<AvailableAllChartType>> = {
  [ChartType.BarLineChart]: {
    id: ChartType.BarLineChart,
    name: 'Simple Chart',
    description: 'A chart that displays Dimensions and Series on the X and Y axis',
    image: '/assets/images/chart-types/vertical_grid.svg',
    min_rows: 3,
    min_cols: 8,
    color: '#D1DEC0',
    show_data: true,
    viz_control_type: ChartControlTypeEnum.Echarts,
    articleId: '360016116918',
    model: new SimpleEchartsSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Segment By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/simple-chart/sc-dimension.svg',
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Dimension,
      },
      breakdowns: {
        label: 'Breakdown By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/simple-chart/sc-breakdown.svg',
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Breakdown,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/simple-chart/sc-serie.svg',
        max: Infinity,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
      },
    },
    validate: (state) => {
      // Additionally throw error when dimensions.length < breakdowns.length
      // Cannot have simple chart without dimension but breakdown.
      const { dimensions, breakdowns } = state.settings;
      if (dimensions.length < breakdowns.length) {
        return { missing: 'dimensions' };
      }

      return validate(state);
    },
    order: 0,
  },
  [ChartType.ScatterChart]: {
    id: ChartType.ScatterChart,
    name: 'Scatter Plot',
    description: 'A scatter plot displays numerical values on X and Y axis',
    image: '/assets/images/chart-types/scatter.svg',
    min_rows: 3,
    min_cols: 8,
    color: '#D1DEC0',
    viz_control_type: ChartControlTypeEnum.Echarts,
    show_data: true,
    articleId: '',
    model: new ScatterSettingsModel(),
    availableAreas: {
      breakdowns: {
        label: 'Segment By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/scatter-chart/scatter-breakdown.svg',
        max: 1,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.ScatterBreakdown,
        dropButtonComponents: ['format', 'label', 'tooltip'],
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/scatter-chart/scatter-serie.svg',
        max: 3,
        min: 2,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
        dropButtonComponents: ['format'],
      },
      size: {
        label: 'Size',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/scatter-chart/scatter-size.svg',
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Scatter,
        dropButtonComponents: ['min_max'],
      },
    },
    order: 1,
    validate,
  },
  [ChartType.Numeric]: {
    id: ChartType.Numeric,
    name: 'Number Box (deprecated)',
    description: 'A Box that can display any number and can be segmented by dates',
    image: '/assets/images/chart-types/number.svg',
    min_rows: 2,
    min_cols: 6,
    color: '#D1DEC0',
    viz_control_type: ChartControlTypeEnum.NumericChart,
    show_data: true,
    articleId: '360016024717',
    model: new NumericWidgetSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Segment By Date',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/numeric-chart/numeric-breakdown.svg',
        max: 1,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/numeric-chart/numeric-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
        dropButtonComponents: ['format', 'filter'],
      },
    },
    order: 2,
    validate,
  },
  [ChartType.NumberBox]: {
    id: ChartType.NumberBox,
    name: 'Number Box',
    description: 'A Box that can display any number and can be segmented by dates',
    image: '/assets/images/chart-types/number.svg',
    min_rows: 2,
    min_cols: 6,
    color: '#D1DEC0',
    viz_control_type: ChartControlTypeEnum.NumberBox,
    show_data: true,
    articleId: '360016024717',
    model: new NumericWidgetSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Segment By Date',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/numeric-chart/numeric-breakdown.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Numeric,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/numeric-chart/numeric-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
        dropButtonComponents: ['format', 'filter'],
      },
    },
    isNew: true,
    order: 2.5,
    validate,
  },
  [ChartType.PieChart]: {
    id: ChartType.PieChart,
    name: 'Pie Chart',
    description: 'Display a pie chart',
    image: '/assets/images/chart-types/pie.svg',
    min_rows: 3,
    min_cols: 6,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.Echarts,
    show_data: true,
    articleId: '',
    model: new PieSettingsModel(),
    availableAreas: {
      breakdowns: {
        label: 'Segment By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/pie-chart/pie-breakdown.svg',
        min: 1,
        max: 1,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/pie-chart/pie-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Pie,
        dropButtonComponents: [],
      },
    },
    validate,
    order: 3,
  },
  [ChartType.TreemapChart]: {
    id: ChartType.TreemapChart,
    name: 'Treemap',
    description: 'Display a treemap chart',
    image: '/assets/images/chart-types/treemap.svg',
    min_rows: 3,
    min_cols: 6,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.Echarts,
    show_data: true,
    articleId: '',
    model: new TreemapSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Segment By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/treemap-chart/treemap-dimension.svg',
        min: 1,
        max: 1,
      },
      breakdowns: {
        label: 'Breakdown By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/treemap-chart/treemap-breakdown.svg',
        max: 1,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/treemap-chart/treemap-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.TreemapMeasure,
        dropButtonComponents: ['format', 'measures_label', 'tooltip'],
      },
    },
    validate,
    order: 4,
  },
  [ChartType.Geo]: {
    id: ChartType.Geo,
    name: 'Geo Map',
    description: 'Display a geo map',
    image: '/assets/images/chart-types/map.svg',
    min_rows: 3,
    min_cols: 6,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.Echarts,
    show_data: true,
    articleId: '',
    model: new GeoSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Segment By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/map-chart/map-dimension.svg',
        min: 1,
        max: 5,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/map-chart/map-serie.svg',
        max: 1,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Aggregation,
      },
    },
    order: 5,
    validate: (state) => {
      const settings = (state as WidgetBuilderModel<ChartType.Geo>).settings;
      const dimensionFields = settings.dimensions.map((dimension) => state.fields.find((f) => f.id === dimension));

      // need at least one geoLocation field
      const geoField = dimensionFields.some((d) => d?.type === ColumnTypeEnum.Geo);

      if (!geoField) {
        return {
          incomplete: `Must include at least one column of type "iso2, region_code or city_ascii"`,
        };
      }

      return validate(state);
    },
  },
  [ChartType.FunnelChart]: {
    id: ChartType.FunnelChart,
    name: 'Funnel Chart',
    description: 'Display a funnel chart',
    image: '/assets/images/chart-types/funnel.svg',
    min_rows: 3,
    min_cols: 6,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.Echarts,
    show_data: true,
    articleId: '',
    model: new FunnelSettingsModel(),
    availableAreas: {
      breakdowns: {
        label: 'Breakdown By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/funnel-chart/funnel-breakdown.svg',
        min: 1,
        max: 1,
      },
      series: {
        label: 'Summarize By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/funnel-chart/funnel-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
        dropButtonComponents: ['format', 'label', 'tooltip'],
      },
    },
    validate,
    order: 6,
  },
};

/*const validateStatistics = (state) => {
  const { chart_type, settings } = state;
  const { availableAreas } = AllChartTypeConstant[chart_type];

  // Ensure each statistics field has conditions or is of type number
  const validateStatisticsField = (areaLabel: string, statisticsField: StatisticsFieldStoreModel | null) =>
    statisticsField?.conditions?.length || statisticsField?.column_type === 'number'
      ? null
      : { incomplete: `Specification for ${areaLabel}` };

  const axisError: ChartValidationResult = Object.entries(availableAreas).reduce(
    (result, [area, cfg]: [ChartTypeConstantAvailableAreas, DropAreaConfigModel]) => {
      const value = settings[area];
      if (Array.isArray(value)) {
        const error = value.reduce(
          (err, v: StatisticsFieldStoreModel) => err || validateStatisticsField(cfg.label, v),
          null,
        );
        return result || error;
      } else {
        const error = validateStatisticsField(cfg.label, value);
        return result || error;
      }
    },
    null as ChartValidationResult,
  );

  return validate(state) || axisError;
};*/

export const OtherChartTypeConstant: Partial<IChartTypeConstant<AvailableAllChartType>> = {
  [ChartType.Table]: {
    id: ChartType.Table,
    name: 'Table',
    description: 'A table that displays in rows and columns',
    image: '/assets/images/chart-types/table.svg',
    min_rows: 8,
    min_cols: 8,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.AgGrid,
    show_data: false,
    articleId: '',
    model: new TableSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Group By',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/table-chart/table-dimension.svg',
        dropButtonType: ColumnDropButtonTypeEnum.Table,
        max: 5,
        min: 1,
      },
      breakdowns: {
        label: 'Segmented By',
        comment: '(max 300 distinct values)',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/pivot-chart/pivot-breakdown.svg',
        max: 2,
        dropButtonType: ColumnDropButtonTypeEnum.TablePivot,
      },
      series: {
        label: 'Columns',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/table-chart/table-serie.svg',
        max: Infinity,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Table,
      },
    },
    order: 7,
    validate: (state) => {
      const settings = (state as WidgetBuilderModel<ChartType.Table>).settings;
      const isSeriesOnly = state.fields.every(
        (field) => !!settings.series.find((seriesField) => seriesField.field_id === field.id),
      );

      if (isSeriesOnly) {
        const config = AllChartTypeConstant[ChartType.Table] as IChartTypeConstantModelBase<ChartType.Table>;
        const areas = config.availableAreas;
        return validate(state, {
          ...areas,
          dimensions: { ...areas.dimensions!, min: 0 },
        });
      }

      return validate(state);
    },
  },
  [ChartType.CohortTable]: {
    id: ChartType.CohortTable,
    name: 'Cohort Table',
    description: 'A cohort table',
    image: '/assets/images/chart-types/cohort.svg',
    min_rows: 12,
    min_cols: 12,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.AgGrid,
    show_data: false,
    articleId: '',
    model: new CohortSettingsModel(),
    availableAreas: {
      dimensions: {
        label: 'Identifier',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/cohort-chart/cohort-identifier.svg',
        min: 1,
        max: 1,
      },
      breakdowns: {
        label: 'Segmented By',
        comment: '(Date)',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/cohort-chart/cohort-dimension.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Aggregation,
      },
      series: {
        label: 'Measures',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/cohort-chart/cohort-serie.svg',
        min: 1,
        max: 1,
        dropButtonType: ColumnDropButtonTypeEnum.Serie,
        dropButtonComponents: ['format'],
      },
    },
    validate,
    order: 8,
  },
  /*[ChartTypeEnum.regression_chart]: {
    id: ChartTypeEnum.regression_chart,
    name: 'Regression',
    description: 'Apply a linear regression to identify causal relationships',
    image: '/assets/images/chart-types/regression.svg',
    min_rows: 8,
    min_cols: 8,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.statistics,
    show_data: false,
    articleId: '360016117638',
    model: new RegressionSettingsModel(),
    availableAreas: {
      y: {
        label: 'Dependent variables',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/regression-chart/regression-x.svg',
        max: 1,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
      x: {
        label: 'Independent variables',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/regression-chart/regression-y.svg',
        min: 1,
        max: 10,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
    },
    validate: validateStatistics,
    order: 10,
  },
  [ChartTypeEnum.regression_z_chart]: {
    id: ChartTypeEnum.regression_z_chart,
    name: 'Standardized Regression',
    description: 'Apply a z-transformed regression to identify causal relationships',
    image: '/assets/images/chart-types/standardized.svg',
    min_rows: 8,
    min_cols: 8,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.statistics,
    show_data: false,
    articleId: '360016027237',
    model: new RegressionSettingsModel(),
    availableAreas: {
      y: {
        label: 'Dependent variables',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/regression-z-chart/regression-z-y.svg',
        max: 1,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
      x: {
        label: 'Independent Standardized Columns',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/regression-z-chart/regression-z-x.svg',
        min: 1,
        max: 10,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
    },
    validate: validateStatistics,
    order: 11,
  },
  [ChartTypeEnum.driver_chart]: {
    id: ChartTypeEnum.driver_chart,
    name: 'Driver',
    description: 'Apply a driver analysis between one dependent and multiply dependent variables',
    image: '/assets/images/chart-types/driver.svg',
    min_rows: 8,
    min_cols: 8,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.statistics,
    show_data: false,
    articleId: '360016027237',
    model: new RegressionSettingsModel(),
    availableAreas: {
      y: {
        label: 'Dependent variables',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/driver-chart/driver-y.svg',
        max: 1,
        min: 1,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
      x: {
        label: 'Drivers',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/driver-chart/driver-x.svg',
        min: 1,
        max: 10,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
    },
    validate: validateStatistics,
    order: 12,
  },
  [ChartTypeEnum.correlation_chart]: {
    id: ChartTypeEnum.correlation_chart,
    name: 'Correlation',
    description: 'Calculate the r-coefficient between one or more variables',
    image: '/assets/images/chart-types/correlation.svg',
    min_rows: 8,
    min_cols: 8,
    color: '#EADBCC',
    viz_control_type: ChartControlTypeEnum.statistics,
    show_data: false,
    articleId: '360016119438',
    model: new RegressionSettingsModel(),
    availableAreas: {
      x: {
        label: 'Correlating Columns',
        icon: '/assets/images/chart-types/chart-type-drop-area-icons/correlation-chart/correlation-x.svg',
        min: 1,
        max: 10,
        dropButtonType: ColumnDropButtonTypeEnum.statistics,
      },
    },
    validate: (state) => {
      const { chart_type, settings } = state;
      const { availableAreas } = AllChartTypeConstant[chart_type];

      const getStatisticsFieldCount = (statisticsField: StatisticsFieldStoreModel): number => {
        if (statisticsField?.column_type === 'number') {
          return 1;
        } else {
          return statisticsField?.conditions?.length || 0;
        }
      };

      const totalCount = Object.keys(availableAreas).reduce((acc, area) => {
        const value: StatisticsFieldStoreModel | StatisticsFieldStoreModel[] | null = settings[area];
        let count: number;
        if (Array.isArray(value)) {
          count = value.reduce((acc, sf) => acc + getStatisticsFieldCount(sf), 0);
        } else {
          count = getStatisticsFieldCount(value);
        }
        return acc + count;
      }, 0);

      const countError: ChartValidationResult =
        totalCount >= 2 ? null : { incomplete: `At least two measures are required` };

      return validate(state) || validateStatistics(state) || countError;
    },
    order: 13,
  },*/
};

export const AllChartTypeConstant = {
  ...EchartTypeConstant,
  ...OtherChartTypeConstant,
};
